import { formatWithCommas } from '@/utils/numberHelpers';
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import type { GiftcardSellRate, GiftcardSellRegion, GiftcardSubcategory } from '../../../../types/ApiTypes';
import Dashboard from "../../../layout/Dashboard";
import { giftcardService } from '../../../services/GiftcardService';
import { ListHead, ListRow, Ticker } from "../../../styles/CrypoAssetStyle";
import { Content, Heading } from "../../../styles/OverviewStyle";
import { RFDesc, RFMain, RFTitle, RFWrap } from "../../../styles/RatesFeesStyle";
import { CloseBtn, H1, Overlay, Panel, Section, TitleRow } from '../../../styles/TransactionDetailStyle';
import { RatesNavigator, TabNavigator } from "../components/Navigator";

export default function SellGiftcardRates() {
   const [data, setData] = useState<GiftcardSellRate[]>([])
   const [loading, setLoading] = useState(true)
   const [open, setOpen] = useState<Record<string, boolean>>({})
   const [selectedRegion, setSelectedRegion] = useState<GiftcardSellRegion | null>(null)

   useEffect(() => {
      loadSellRates()
   }, [])

   const loadSellRates = async () => {
      try {
         const response = await giftcardService.getSellRates()
         if (response.status === 'success' && response.data) {
            setData(response.data)
         }
      } catch (error) {
         console.error('Failed to load sell rates:', error)
      } finally {
         setLoading(false)
      }
   }

   const toggle = (k: string) => setOpen(o => ({ ...o, [k]: !o[k] }))

   return (
      <Dashboard title="Rates & Fees / Rates / Sell giftcard" icon="solar:tag-price-linear" active="rates_fees">
         <Content>
            <Heading>Rates & Fees</Heading>

            <TabNavigator active="rates" />

            <RFWrap>
               <RatesNavigator active="sell_giftcard" />

               <RFMain>
                  <div>
                     <RFTitle>Sell giftcard</RFTitle>
                     <RFDesc>
                        Here you can set and manage the sell rates for gift cards manually. Adjust the values to reflect your business model and regional requirements. Changes made here will take effect immediately for your users
                     </RFDesc>

                     {loading ? (
                        <div className="flex justify-center py-12">
                           <Icon icon="svg-spinners:ring-resize" width={32} className="text-[#C02236]" />
                        </div>
                     ) : (
                        <>
                           <ListHead style={{ gridTemplateColumns: '1fr 150px' }}>
                              <div>Card</div>
                              <div></div>
                           </ListHead>

                           {data.map(card => (
                              <div key={card.id}>
                                 <ListRow style={{ gridTemplateColumns: '1fr 150px' }}>
                                    <div className="flex items-center gap-3">
                                       <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#F1F1F3] overflow-hidden">
                                          <img src={card.logoUrl} alt={card.type} className="w-full h-full object-cover" />
                                       </span>
                                       <span className="text-sm text-[#101018]">{card.type}</span>
                                    </div>
                                    <div className="flex items-center justify-end">
                                       <Icon
                                          icon={open[card.id] ? 'solar:alt-arrow-up-bold' : 'solar:alt-arrow-down-bold'}
                                          width={14}
                                          onClick={() => toggle(card.id)}
                                          className={`cursor-pointer ${card.regions.length > 0 ? 'text-[#C02236]' : 'text-[#CACACC]'}`}
                                       />
                                    </div>
                                 </ListRow>

                                 {open[card.id] && card.regions.length > 0 && (
                                    <div className="mb-3 bg-[#FBFBFB] rounded-b-lg">
                                       {card.regions.map((region) => (
                                          <div key={region.id} className='flex items-center py-3 pr-2'>
                                             <div className="flex-1 flex items-center gap-2 text-[#858589] text-sm pl-8">
                                                <img src={region.flagUrl} alt={region.countryName} className="w-4 h-3 object-cover rounded-sm" />
                                                <span>{region.countryName}</span>
                                                <Ticker style={{ color: '#6D6D72' }}>{region.countryCode}</Ticker>
                                             </div>
                                             <div className="w-[100px] flex items-center justify-end gap-4 pl-4">
                                                <button
                                                   className="text-[#C02236] text-xs hover:underline cursor-pointer"
                                                   onClick={() => setSelectedRegion(region)}
                                                >
                                                   Manage
                                                </button>
                                             </div>
                                          </div>
                                       ))}
                                    </div>
                                 )}
                              </div>
                           ))}
                        </>
                     )}
                  </div>
               </RFMain>
            </RFWrap>
         </Content>

         {selectedRegion && (
            <SubcategoryOverlay
               region={selectedRegion}
               onClose={() => setSelectedRegion(null)}
               onUpdate={loadSellRates}
            />
         )}
      </Dashboard>
   )
}

function SubcategoryOverlay({ region, onClose, onUpdate }: { region: GiftcardSellRegion; onClose: () => void; onUpdate: () => void }) {
   const [subcategories, setSubcategories] = useState<GiftcardSubcategory[]>(region.subcategories)
   const [editing, setEditing] = useState<string | null>(null)
   const [creating, setCreating] = useState(false)
   const [form, setForm] = useState({ name: '', minAmount: '', maxAmount: '', sellRate: '' })

   useEffect(() => {
      function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
      window.addEventListener('keydown', onEsc)
      return () => window.removeEventListener('keydown', onEsc)
   }, [onClose])

   const startCreate = () => {
      setCreating(true)
      setForm({ name: '', minAmount: '', maxAmount: '', sellRate: '' })
   }

   const startEdit = (sub: GiftcardSubcategory) => {
      setEditing(sub.id)
      setForm({ name: sub.name, minAmount: sub.minAmount, maxAmount: sub.maxAmount, sellRate: sub.sellRate })
   }

   const cancelEdit = () => {
      setEditing(null)
      setCreating(false)
      setForm({ name: '', minAmount: '', maxAmount: '', sellRate: '' })
   }

   const handleCreate = async () => {
      try {
         const response = await giftcardService.createSubcategory(region.id, {
            name: form.name,
            minAmount: parseFloat(form.minAmount),
            maxAmount: parseFloat(form.maxAmount),
            sellRate: parseFloat(form.sellRate)
         })
         if (response.status === 'success') {
            const newSub: GiftcardSubcategory = {
               id: response.data?.id || Date.now().toString(),
               name: form.name,
               minAmount: form.minAmount,
               maxAmount: form.maxAmount,
               sellRate: form.sellRate
            }
            setSubcategories(prev => [...prev, newSub])
         }
         onUpdate()
         cancelEdit()
      } catch (error) {
         console.error('Failed to create subcategory:', error)
      }
   }

   const handleUpdate = async (id: string) => {
      try {
         await giftcardService.updateSubcategory(id, {
            name: form.name,
            minAmount: parseFloat(form.minAmount),
            maxAmount: parseFloat(form.maxAmount),
            sellRate: parseFloat(form.sellRate)
         })
         setSubcategories(prev => prev.map(s => s.id === id ? {
            ...s,
            name: form.name,
            minAmount: form.minAmount,
            maxAmount: form.maxAmount,
            sellRate: form.sellRate
         } : s))
         onUpdate()
         cancelEdit()
      } catch (error) {
         console.error('Failed to update subcategory:', error)
      }
   }

   const handleDelete = async (id: string) => {
      try {
         await giftcardService.deleteSubcategory(id)
         setSubcategories(prev => prev.filter(s => s.id !== id))
         onUpdate()
      } catch (error) {
         console.error('Failed to delete subcategory:', error)
      }
   }

   return (
      <Overlay onClick={onClose}>
         <Panel onClick={e => e.stopPropagation()}>
            <TitleRow>
               <H1>Manage Subcategories</H1>
               <CloseBtn onClick={onClose}><Icon icon="material-symbols:close" width={20} /></CloseBtn>
            </TitleRow>

            <Section>
               <div className='text-[12px] tracking-tight text-[#9C9C9F] mb-2'>Region</div>
               <div className='flex items-center gap-2'>
                  <img src={region.flagUrl} alt={region.countryName} className="w-5 h-4 object-cover rounded-sm" />
                  <span className='text-sm text-[#101018]'>{region.countryName}</span>
               </div>
            </Section>

            <Section>
               <div className='flex items-center justify-between mb-3'>
                  <div className='text-[12px] tracking-tight text-[#9C9C9F]'>Subcategories</div>
                  <button onClick={startCreate} className='px-3 py-1.5 text-xs rounded-md bg-[#C02236] text-white hover:bg-[#9a1a2a]'>
                     Add New
                  </button>
               </div>

               <div className='space-y-2'>
                  {creating && (
                     <div className='p-3 bg-[#F9F9FA] rounded-lg space-y-2'>
                        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder='Name' className='w-full h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                        <div className='grid grid-cols-3 gap-2'>
                           <input value={form.minAmount} onChange={e => setForm(f => ({ ...f, minAmount: e.target.value }))} placeholder='Min' className='h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                           <input value={form.maxAmount} onChange={e => setForm(f => ({ ...f, maxAmount: e.target.value }))} placeholder='Max' className='h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                           <input value={form.sellRate} onChange={e => setForm(f => ({ ...f, sellRate: e.target.value }))} placeholder='Rate' className='h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                        </div>
                        <div className='flex gap-2 justify-end'>
                           <button onClick={cancelEdit} className='cursor-pointer'><Icon icon="mdi:close" width={16} className="text-[#DD524D] cursor-pointer" /></button>
                           <button onClick={handleCreate} className='cursor-pointer'><Icon icon="mdi:check" width={16} className="text-[#4CAF50] cursor-pointer" /></button>
                        </div>
                     </div>
                  )}

                  {subcategories.map(sub => (
                     <div key={sub.id} className='p-3 bg-[#F9F9FA] rounded-lg'>
                        {editing === sub.id ? (
                           <div className='space-y-2'>
                              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className='w-full h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                              <div className='grid grid-cols-3 gap-2'>
                                 <input value={form.minAmount} onChange={e => setForm(f => ({ ...f, minAmount: e.target.value }))} className='h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                                 <input value={form.maxAmount} onChange={e => setForm(f => ({ ...f, maxAmount: e.target.value }))} className='h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                                 <input value={form.sellRate} onChange={e => setForm(f => ({ ...f, sellRate: e.target.value }))} className='h-8 px-3 rounded-md border border-[#E7E7E9] text-sm outline-none focus:border-[#C02236]' />
                              </div>
                              <div className='flex gap-2 justify-end'>
                                 <button onClick={cancelEdit} className='cursor-pointer'><Icon icon="mdi:close" width={16} className="text-[#DD524D] cursor-pointer" /></button>
                                 <button onClick={() => handleUpdate(sub.id)} className='cursor-pointer'><Icon icon="mdi:check" width={16} className="text-[#4CAF50] cursor-pointer" /></button>
                              </div>
                           </div>
                        ) : (
                           <div className='flex items-center justify-between'>
                              <div>
                                 <div className='text-sm text-[#101018] font-medium'>{sub.name}</div>
                                 <div className='text-xs text-[#6D6D72] mt-1'>${sub.minAmount} - ${sub.maxAmount} • Rate: ₦{formatWithCommas(sub.sellRate)}</div>
                              </div>
                              <div className='flex gap-3'>
                                 <button onClick={() => startEdit(sub)} className='text-[#C02236] text-xs hover:underline cursor-pointer'>Edit</button>
                                 <button onClick={() => handleDelete(sub.id)} className='text-[#DD524D] text-xs hover:underline cursor-pointer'>Delete</button>
                              </div>
                           </div>
                        )}
                     </div>
                  ))}
               </div>
            </Section>
         </Panel>
      </Overlay>
   )
}
